// src/hooks/useLiveTicket.js
// ─────────────────────────────────────────────────────────────────────────────
// The live QR for one ticket in the browser, redrawn every second
// (16 Oct 2026). The apps' app/components/ticketing/useLiveTicket.js, for the
// web.
//
// Modes:
//   live      this browser holds the ticket: a QR that changes every 15
//             seconds and a 6-digit code that changes every minute
//   elsewhere the ticket is live but shown on another device (or on none yet);
//             `moveHere` brings it here, and the other device's QR stops
//   static    no live key anywhere (an older server, or a ticket not yet
//             live): the old QR, drawn as before
//
// Works offline once the key is stored (src/utils/liveTicketStore.js).
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";

import { liveFrame } from "../utils/liveTicket";
import { loadLiveKey, syncLiveKey } from "../utils/liveTicketStore";
import { apiMoveTicketHere } from "../api/ticketingApi";

const MOVE_FAILED = "Could not show the ticket on this device. Check your connection and try again.";

export default function useLiveTicket({ ticketId, ticket, onMoved }) {
  const [record, setRecord] = useState(null);
  const [frame, setFrame] = useState(null);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState("");
  const onMovedRef = useRef(onMoved);
  onMovedRef.current = onMoved;

  const hasTicket = Boolean(ticket);
  const ticketKey = (ticket && ticket.live && ticket.live.key) || null;
  const heldElsewhere = Boolean(ticket && ticket.liveHeldElsewhere);
  const liveRequired = Boolean(ticket && ticket.liveRequired);
  const status = (ticket && ticket.status) || null;

  // What the server said wins; with nothing from the server, the stored key.
  useEffect(() => {
    if (!ticketId || !hasTicket) {
      setRecord(null);
      return;
    }
    const synced = syncLiveKey({ ...ticket, _id: ticket._id || ticketId });
    setRecord(synced || (!heldElsewhere && status === "VALID" ? loadLiveKey(ticketId) : null));
    // `ticket` is read through the fields below; a re-render with the same
    // ticket must not rewrite storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, hasTicket, ticketKey, heldElsewhere, status]);

  // Redraw every second while there is a key: the QR every 15 s, the
  // countdown always.
  useEffect(() => {
    if (!record || !record.key) {
      setFrame(null);
      return undefined;
    }
    const draw = () => {
      try {
        setFrame(liveFrame(record, String(ticketId)));
      } catch (e) {
        setFrame(null);
      }
    };
    draw();
    const timer = setInterval(draw, 1000);
    return () => clearInterval(timer);
  }, [record, ticketId]);

  const moveHere = useCallback(async () => {
    if (!ticketId || moving) return;
    setMoving(true);
    setMoveError("");
    try {
      const d = await apiMoveTicketHere(ticketId);
      const moved = d && d.ticket;
      if (moved && moved.live && moved.live.key) {
        setRecord(syncLiveKey(moved));
        if (onMovedRef.current) onMovedRef.current(moved);
      } else {
        setMoveError(MOVE_FAILED);
      }
    } catch (err) {
      setMoveError((err && err.response && err.response.data && err.response.data.message) || MOVE_FAILED);
    } finally {
      setMoving(false);
    }
  }, [ticketId, moving]);

  let mode = "static";
  if (status === "VALID" && record && record.key) mode = "live";
  else if (status === "VALID" && (heldElsewhere || liveRequired)) mode = "elsewhere";

  return { mode, frame, moveHere, moving, moveError };
}
